import {HttpEvent, HttpInterceptorFn, HttpRequest} from '@angular/common/http';
import {inject} from "@angular/core";
import {catchError, from, Observable, switchMap, throwError} from "rxjs";
import {AuthService, Tokens} from "./services/auth/auth.service";
import {environment} from "../../environments/environment";

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(environment.apiUrl)) {
    return next(req);
  }

  const authService = inject(AuthService);

  const send = (request: HttpRequest<unknown>, tokens: Tokens | undefined): Observable<HttpEvent<unknown>> =>
    next(tokens ? request.clone({setHeaders: {Authorization: `Bearer ${tokens.accessToken}`}}) : request);

  const logoutAndFail = (): Observable<HttpEvent<unknown>> => {
    authService.onUnauthorized();
    // Throw a plain Error so GlobalErrorHandle surfaces this message verbatim
    // as a toast (HttpErrorResponse would be mapped to a generic "Something
    // went wrong").
    return throwError(() => new Error('Session expired, please log in again'));
  };

  return from(authService.getTokens()).pipe(
    switchMap(tokens => send(req, tokens).pipe(
      catchError(error => {
        // 403 is a permission error — the user is authenticated but not allowed
        // to do this. Don't touch the session; let the caller handle it.
        if (error.status !== 401) {
          return throwError(() => error);
        }

        // 401 without prior tokens means the request was anonymous to begin
        // with. There's nothing to refresh and no session to end.
        if (!tokens) {
          return throwError(() => error);
        }

        // 401 with tokens: try one force-refresh + retry before giving up.
        // The token may have expired between getTokens() and the request, or
        // Cognito rotated/revoked it. Only log out if the refresh itself fails
        // or the retried request also returns 401.
        return from(authService.forceRefresh()).pipe(
          switchMap(refreshed => {
            if (!refreshed) {
              return logoutAndFail();
            }
            return send(req, refreshed).pipe(
              catchError(retryError => retryError.status === 401
                ? logoutAndFail()
                : throwError(() => retryError))
            );
          })
        );
      })
    ))
  );
};
